import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { CandidateConsentBanner } from '@/features/recruitment/components/candidate-consent-banner';
import { CandidateProfileHeader } from '@/features/recruitment/components/candidate-profile-header';
import { CandidateProfileTabs } from '@/features/recruitment/components/candidate-profile-tabs';
import { CandidateParseJobProvider } from '@/features/recruitment/context/candidate-parse-job-context';
import { useCandidate } from '@/hooks/use-candidates';
import { canViewCandidates } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';

export const Route = createFileRoute('/recruitment/candidates_/$id')({
  beforeLoad: () => {
    const checkGate = () => {
      const { isAuthenticated, user } = useAuthStore.getState();
      if (!isAuthenticated) throw redirect({ to: '/login' });
      if (!canViewCandidates(user)) throw redirect({ to: '/people' });
    };
    if (useAuthStore.getState().isInitialized) {
      checkGate();
      return;
    }
    return useAuthStore.getState().initialize().then(checkGate);
  },
  component: CandidateProfileLayout,
});

function CandidateProfileLayout() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: candidate, isLoading, error } = useCandidate(id);

  const handleBack = () => navigate({ to: '/recruitment/candidates' });

  return (
    <AuthGuard>
      <AppLayout>
        <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <span>Recruitment</span>
            <span>/</span>
            <button
              onClick={handleBack}
              className="cursor-pointer transition-colors hover:text-foreground"
            >
              Candidates
            </button>
            <span>/</span>
            <span
              className="max-w-xs truncate font-medium text-foreground"
              title={candidate?.fullName}
            >
              {candidate?.fullName ?? 'Candidate'}
            </span>
          </div>
        </header>

        {candidate ? (
          <CandidateParseJobProvider candidateId={id}>
            <div className="px-6 pt-5">
              <button
                onClick={handleBack}
                className="mb-4 -ml-2 inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to Candidates
              </button>
              <CandidateProfileHeader candidate={candidate} />
              <CandidateConsentBanner candidate={candidate} />
            </div>

            <CandidateProfileTabs candidateId={id} />

            <div className="px-6 pb-8 pt-6">
              <Outlet />
            </div>
          </CandidateParseJobProvider>
        ) : (
          <div className="p-6">
            {isLoading && <div className="text-muted-foreground">Loading...</div>}
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                Failed to load candidate.
              </div>
            )}
          </div>
        )}
      </AppLayout>
    </AuthGuard>
  );
}
